/**
 * PreVisitIntakeSuggestionCard — Renderer für 'pre_visit_intake_dispatched'-
 * Suggestions aus cap-dispatch-pre-visit-intake.
 *
 * Zeigt:
 *   - Termin-Datum (kommendes Erstgespräch in 2-14 Tagen)
 *   - Token-Link mit Copy-Button (Tooltip "Link in die Zwischenablage")
 *   - Ablaufdatum
 *   - Rationale (warum dieses Intake jetzt)
 *
 * Aktionen:
 *   - "Patientenakte öffnen" → navigiert auf /patients/${patient_id}
 *   - Dismiss-Button (X) → resolve(dismissed)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardCheck,
  Copy,
  Mail,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isPreVisitIntakePayload,
  type AiSuggestion,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

export const PreVisitIntakeSuggestionCard: React.FC<Props> = ({
  suggestion,
}) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();
  const [copied, setCopied] = useState(false);

  if (!isPreVisitIntakePayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}`);
    }
    await resolve(suggestion, 'accepted', 'open_patient');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(p.intake_url);
      setCopied(true);
      toast.success('Intake-Link kopiert.');
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Kopieren fehlgeschlagen.');
    }
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('de-DE') : '—';

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-medium">Pre-Visit-Intake</span>
          <span className="text-xs text-muted-foreground">
            Termin {fmtDate(p.appointment_start_time)}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <p className="mb-2 text-sm text-foreground">{p.rationale}</p>

      <div className="mb-2 flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1.5">
        <code className="flex-1 truncate text-[11px] font-mono text-muted-foreground">
          {p.intake_url}
        </code>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Intake-Link kopieren"
                onClick={handleCopy}
                className="h-6 w-6 shrink-0"
              >
                {copied ? (
                  <ClipboardCheck className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Link in die Zwischenablage</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <p className="mb-3 text-xs text-muted-foreground">
        Gültig bis: {fmtDate(p.expires_at)}
      </p>

      <Button size="sm" onClick={handlePrimary}>
        Patientenakte öffnen
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </article>
  );
};
